/**
 * 触屏机的运行配置。
 *
 * 全部走环境变量，**不写死在代码里** —— 因为同一份构建产物要能装到不同门店、
 * 不同机器上（店码不同、设备号不同、开发机允许测试收款而门店不允许）。
 *
 * 关键的一条：`allowTestPay` 默认 **false**。
 * 也就是说，部署到门店的触屏机默认**不显示**「测试确认收款」按钮，
 * 必须由部署的人显式打开才会出现，而且界面上会一直挂着红底提示。
 */

export interface KioskConfig {
  /** 接口根地址。默认同源 /api/v1（开发时由 Vite 代理到后端） */
  apiBase: string;
  /** 门店编码，同一个后端服务多家店时靠它区分 */
  storeCode: string;
  /** 本台触屏机的设备标识，写进顾客标识里，便于事后追溯是哪台机器下的单 */
  deviceId: string;
  /** 是否允许显示「测试确认收款」按钮（只用于开发/演示联调） */
  allowTestPay: boolean;
  /** 无人操作多少秒后提醒「还在吗」 */
  idleWarnSeconds: number;
  /** 提醒后再无操作多少秒就自动清空回首页 */
  idleResetSeconds: number;
  /** 出取餐号页面停留多少秒后自动回首页（下一位顾客要用） */
  doneAutoBackSeconds: number;
  /**
   * 运行模式。
   *   kiosk —— 店里那台一体机：靠设备号认顾客，没有桌号，出取餐号
   *   table —— 顾客用自己手机**扫桌上的二维码**进来：靠令牌认桌号
   *
   * 两种模式共用同一套点餐界面和下单接口，区别只在「凭据」和「要不要桌号」。
   * 后端本来就把凭据建模成 openId | qrToken 二选一，所以这里不需要第二套前端。
   */
  mode: 'kiosk' | 'table';
  /** 桌台令牌（table 模式才有；kiosk 模式为 null） */
  tableToken: string | null;
}

export const DEFAULT_CONFIG: Omit<KioskConfig, 'deviceId'> = {
  apiBase: '/api/v1',
  storeCode: 'FS001',
  allowTestPay: false,
  idleWarnSeconds: 60,
  idleResetSeconds: 20,
  /** 出取餐号后停留多久自动回首页。**别调太短**：顾客要扫码把小票存进手机 */
  doneAutoBackSeconds: 60,
  mode: 'kiosk',
  tableToken: null,
};

/** 把字符串变成安全的整数（环境变量永远是字符串，而且可能写错） */
function toPositiveInt(value: string | undefined, fallback: number): number {
  if (value === undefined || value.trim() === '') return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.floor(parsed);
}

/**
 * 组装配置。
 * 抽成纯函数（env 由调用方传入）是为了能直接单元测试，不用真的跑浏览器。
 */
export function resolveKioskConfig(
  env: Record<string, string | undefined>,
  deviceId: string,
  search?: string,
): KioskConfig {
  const apiBase = env.VITE_API_BASE?.trim();
  const storeCode = env.VITE_STORE_CODE?.trim();
  // 只有拿到有效桌台令牌才进 table 模式 —— 没令牌时行为和以前完全一样，
  // 所以这个改动对已经在门店跑着的那台一体机是**零影响**的。
  const tableToken = parseTableToken(search);
  return {
    mode: tableToken ? 'table' : 'kiosk',
    tableToken,
    apiBase: apiBase && apiBase !== '' ? apiBase : DEFAULT_CONFIG.apiBase,
    storeCode: storeCode && storeCode !== '' ? storeCode : DEFAULT_CONFIG.storeCode,
    deviceId,
    // 只有显式写成 'true' 才打开 —— '1'、'yes' 一律不算，避免误开
    allowTestPay: env.VITE_KIOSK_ALLOW_TEST_PAY === 'true',
    idleWarnSeconds: toPositiveInt(env.VITE_KIOSK_IDLE_WARN_SECONDS, DEFAULT_CONFIG.idleWarnSeconds),
    idleResetSeconds: toPositiveInt(
      env.VITE_KIOSK_IDLE_RESET_SECONDS,
      DEFAULT_CONFIG.idleResetSeconds,
    ),
    doneAutoBackSeconds: toPositiveInt(
      env.VITE_KIOSK_DONE_BACK_SECONDS,
      DEFAULT_CONFIG.doneAutoBackSeconds,
    ),
  };
}

/**
 * 从 URL 查询串里解析桌台令牌。
 *
 * 兼容三种写法（和微信小程序的入口参数保持一致，同一张二维码两边都能用）：
 *   ?t=xxx        普通链接二维码 / 我们自己生成的桌贴
 *   ?qrToken=xxx  直接命名
 *   ?scene=xxx    小程序码的 scene（可能是 URL 编码过的，也可能形如 "t=xxx"）
 *
 * 抽成纯函数是为了能直接单元测试 —— 这段逻辑出错的表现是
 * 「顾客扫了码却提示没有桌号」，在现场很难查，所以必须有测试钉住。
 */
export function parseTableToken(search: string | undefined): string | null {
  if (!search) return null;
  const cleaned = search.startsWith('?') ? search.slice(1) : search;
  if (cleaned.trim() === '') return null;

  const params = new Map<string, string>();
  for (const pair of cleaned.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    if (eq === -1) continue;
    const key = pair.slice(0, eq);
    let value = pair.slice(eq + 1);
    try {
      value = decodeURIComponent(value.replace(/\+/g, ' '));
    } catch {
      /* 解不开就用原值 */
    }
    if (!params.has(key)) params.set(key, value);
  }

  // ⚠️ 这里不能用 `params.get('t') ?? params.get('qrToken')`：
  //    如果地址是 ?t=&qrToken=real-one，get('t') 返回的是**空字符串**（不是 null/undefined），
  //    ?? 不会往后 fallback，于是一个空值把后面有效的令牌整个挡掉了，
  //    表现就是「码上有令牌，页面却说没有桌号」。必须显式跳过空值。
  const direct = [params.get('t'), params.get('qrToken')].find(
    (value) => value !== undefined && value.trim() !== '',
  );
  if (direct) return direct.trim();

  // scene：小程序码里是整段编码过的，可能形如 "t=xxx"
  const scene = params.get('scene');
  if (scene && scene.trim() !== '') {
    const inner = parseTableToken(scene.replace(/^\?/, ''));
    if (inner) return inner;
    if (!scene.includes('=')) return scene.trim();
  }
  return null;
}

const DEVICE_ID_KEY = 'jinhua.kiosk.deviceId';

/** 最小可用的存储接口（方便测试里塞一个假的） */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** 生成一个随机标识（不用 crypto.randomUUID 是为了兼容老旧的内嵌浏览器） */
export function randomToken(length = 12): string {
  const alphabet = 'abcdefghijkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

/**
 * 取本机设备号：存在就用原来的，不存在就生成一个存下来。
 *
 * 为什么设备号要持久化：它是「哪台机器下的单」的唯一线索。
 * 如果每次刷新都变，出了纠纷就查不出是前厅那台还是侧门那台下的单。
 */
export function ensureDeviceId(store: KeyValueStore | null): string {
  if (store) {
    const existing = store.getItem(DEVICE_ID_KEY);
    if (existing && existing.trim() !== '') return existing;
  }
  const created = `k${randomToken(10)}`;
  store?.setItem(DEVICE_ID_KEY, created);
  return created;
}
